/**
 * Saved Views Types
 * 
 * Defines types for saving and persisting view configurations
 * (filters, sort, columns, etc.) that can be accessed from navigation.
 */

import type { ViewMode, SortState, ActiveFilter } from '../../components/collection/types';

// ============================================================================
// Core Types
// ============================================================================

/**
 * Entity types that can have saved views
 * @deprecated 'candidates' — use 'items' for new views; kept for backward compatibility with existing saved_views rows
 */
export type SavedViewEntityType = 
  | 'items'
  | 'candidates' 
  | 'submissions' 
  | 'sessions' 
  | 'focus_lists'
  | 'cycles';

/**
 * Visibility levels for saved views
 */
export type SavedViewVisibility = 'private' | 'team' | 'org';

/**
 * View status
 */
export type SavedViewStatus = 'active' | 'archived';

/**
 * Column configuration for table views
 */
export interface SavedViewColumn {
  /** Column key */
  key: string;
  
  /** Column label */
  label: string;
  
  /** Column width (pixels) */
  width?: number;
  
  /** Whether column is visible */
  visible: boolean;
  
  /** Sort order for column arrangement */
  order: number;
}

/**
 * Quick filter preset
 */
export interface QuickFilter {
  /** Unique ID */
  id: string;
  
  /** Display label */
  label: string;
  
  /** Filter to apply */
  filter: ActiveFilter<string>;
}

/**
 * Saved View definition
 */
export interface SavedView {
  /** Unique identifier */
  id: string;
  
  /** View name */
  name: string;
  
  /** Description */
  description?: string;
  
  /** Entity type this view applies to */
  entityType: SavedViewEntityType;
  
  // ============================================================================
  // View Configuration
  // ============================================================================
  
  /** Active filters */
  filters: ActiveFilter<string>[];
  
  /** Sort configuration */
  sort: SortState<string>;
  
  /** Group by field */
  groupBy?: string;
  
  /** View mode (list, card, table, kanban) */
  viewMode: ViewMode;
  
  /** Column configuration (for table view) */
  columns?: SavedViewColumn[];
  
  /** Quick filter presets */
  quickFilters?: QuickFilter[];
  
  /** Search query (if saved) */
  searchQuery?: string;
  
  // ============================================================================
  // Navigation
  // ============================================================================
  
  /** Lucide icon name */
  icon?: string;
  
  /** Navigation section (e.g., 'intake', 'prioritize', 'analyze') */
  navSection?: string;
  
  /** Order within section */
  navOrder?: number;
  
  /** Whether pinned to sidebar */
  isPinned?: boolean;
  
  /** Badge count (dynamic, not persisted) */
  badgeCount?: number;
  
  // ============================================================================
  // Permissions
  // ============================================================================
  
  /** Visibility scope */
  visibility: SavedViewVisibility;
  
  /** User ID of creator */
  createdBy: string;
  
  /** User name of creator */
  createdByName?: string;
  
  /** Team/workspace ID (for team visibility) */
  teamId?: string;
  
  /** Whether this is a system view (cannot be deleted) */
  isSystem?: boolean;
  
  /** Whether this is the default view for the entity type */
  isDefault?: boolean;
  
  // ============================================================================
  // Metadata
  // ============================================================================
  
  /** View status */
  status: SavedViewStatus;
  
  /** Workspace ID */
  workspaceId?: string;
  
  /** Creation timestamp */
  createdAt: Date;
  
  /** Last update timestamp */
  updatedAt: Date;
  
  /** Last accessed timestamp */
  lastAccessedAt?: Date;
  
  /** Access count */
  accessCount?: number;
  
  /** Custom metadata */
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new saved view with defaults
 */
export function createSavedView(
  params: Pick<SavedView, 'name' | 'entityType' | 'createdBy'> & Partial<SavedView>
): SavedView {
  const now = new Date();
  
  return {
    // saved_views.id is uuid: a non-uuid id made every insert fail (22P02).
    id: params.id || crypto.randomUUID(),
    name: params.name,
    entityType: params.entityType,
    filters: params.filters || [],
    sort: params.sort || { field: 'createdAt', direction: 'desc' },
    viewMode: params.viewMode || 'list',
    visibility: params.visibility || 'private',
    createdBy: params.createdBy,
    status: params.status || 'active',
    createdAt: params.createdAt || now,
    updatedAt: params.updatedAt || now,
    ...params,
  };
}

// ============================================================================
// Legacy label mapping (Candidate → Item)
// ============================================================================

/** Map legacy saved-view names to current terminology for display. */
export function displayViewName(name: string): string {
  if (name === 'All Candidates') return 'All Items';
  return name;
}

/**
 * Resolve items list path: 'candidates' (legacy) and 'items' both go to /prioritization/items
 */
export function itemsViewPath(entityType: SavedViewEntityType): string {
  return entityType === 'candidates' || entityType === 'items' ? 'items' : entityType;
}

// ============================================================================
// System Views (Built-in)
// ============================================================================

/**
 * Built-in system views that cannot be deleted
 */
export const SYSTEM_VIEWS: Omit<SavedView, 'id' | 'createdAt' | 'updatedAt'>[] = [
  // Items views (formerly "candidates")
  {
    name: 'All Items',
    entityType: 'items',
    filters: [],
    sort: { field: 'updatedAt', direction: 'desc' },
    viewMode: 'table',
    visibility: 'org',
    createdBy: 'system',
    isSystem: true,
    isDefault: true,
    icon: 'List',
    navSection: 'prioritize',
    navOrder: 1,
    status: 'active',
  },
  {
    name: 'Ready for Ranking',
    entityType: 'items',
    filters: [
      {
        id: 'ready-filter',
        facetKey: 'readyForRanking',
        operator: 'equals',
        value: 'true',
        label: 'Ready for Ranking: Yes',
      },
    ],
    sort: { field: 'updatedAt', direction: 'desc' },
    viewMode: 'table',
    visibility: 'org',
    createdBy: 'system',
    isSystem: true,
    icon: 'CheckCircle',
    navSection: 'prioritize',
    navOrder: 2,
    status: 'active',
  },
  {
    name: 'Needs Definition',
    entityType: 'items',
    filters: [
      {
        id: 'shaping-filter',
        facetKey: 'exit_status',
        operator: 'in',
        value: ['RAW', 'INTAKE_PENDING', 'SHAPING'],
        label: 'Needs Definition',
      },
    ],
    sort: { field: 'createdAt', direction: 'desc' },
    viewMode: 'list',
    visibility: 'org',
    createdBy: 'system',
    isSystem: true,
    icon: 'Hammer',
    navSection: 'intake',
    navOrder: 1,
    status: 'active',
  },
  
  // Submissions views
  {
    name: 'My Submissions',
    entityType: 'submissions',
    filters: [], // Filtered by current user in query
    sort: { field: 'submittedAt', direction: 'desc' },
    viewMode: 'list',
    visibility: 'private',
    createdBy: 'system',
    isSystem: true,
    icon: 'User',
    navSection: 'intake',
    navOrder: 2,
    status: 'active',
  },
  {
    name: 'Intake Queue',
    entityType: 'submissions',
    filters: [
      {
        id: 'status-filter',
        facetKey: 'status',
        operator: 'in',
        value: ['INTAKE_PENDING', 'SHAPING'],
        label: 'Status: Pending/Item Work',
      },
    ],
    sort: { field: 'submittedAt', direction: 'desc' },
    viewMode: 'list',
    visibility: 'org',
    createdBy: 'system',
    isSystem: true,
    icon: 'Inbox',
    navSection: 'intake',
    navOrder: 0,
    status: 'active',
  },
];

// ============================================================================
// Supabase Conversion Helpers
// ============================================================================

/**
 * Convert SavedView to Supabase row format
 */
export function toSupabaseFormat(view: SavedView): Record<string, unknown> {
  return {
    id: view.id,
    name: view.name,
    description: view.description,
    entity_type: view.entityType,
    // jsonb columns take the values as-is. JSON.stringify here stored a JSON
    // *string* inside jsonb (double-encoded), which only this app could read back.
    filters: view.filters,
    sort: view.sort,
    group_by: view.groupBy,
    view_mode: view.viewMode,
    columns: view.columns ?? null,
    quick_filters: view.quickFilters ?? null,
    search_query: view.searchQuery,
    icon: view.icon,
    nav_section: view.navSection,
    nav_order: view.navOrder,
    is_pinned: view.isPinned,
    visibility: view.visibility,
    created_by: view.createdBy,
    created_by_name: view.createdByName,
    team_id: view.teamId,
    is_system: view.isSystem,
    is_default: view.isDefault,
    status: view.status,
    workspace_id: view.workspaceId,
    created_at: view.createdAt.toISOString(),
    updated_at: view.updatedAt.toISOString(),
    last_accessed_at: view.lastAccessedAt?.toISOString(),
    access_count: view.accessCount,
    metadata: view.metadata ?? null,
  };
}

/**
 * Convert Supabase row to SavedView
 */
export function fromSupabaseFormat(row: Record<string, unknown>): SavedView {
  return {
    id: row.id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    entityType: row.entity_type as SavedViewEntityType,
    filters: parseJson(row.filters, []),
    sort: parseJson(row.sort, { field: 'createdAt', direction: 'desc' }),
    groupBy: row.group_by as string | undefined,
    viewMode: row.view_mode as ViewMode,
    columns: parseJson(row.columns, undefined),
    quickFilters: parseJson(row.quick_filters, undefined),
    searchQuery: row.search_query as string | undefined,
    icon: row.icon as string | undefined,
    navSection: row.nav_section as string | undefined,
    navOrder: row.nav_order as number | undefined,
    isPinned: row.is_pinned as boolean | undefined,
    visibility: row.visibility as SavedViewVisibility,
    createdBy: row.created_by as string,
    createdByName: row.created_by_name as string | undefined,
    teamId: row.team_id as string | undefined,
    isSystem: row.is_system as boolean | undefined,
    isDefault: row.is_default as boolean | undefined,
    status: row.status as SavedViewStatus,
    workspaceId: row.workspace_id as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    lastAccessedAt: row.last_accessed_at ? new Date(row.last_accessed_at as string) : undefined,
    accessCount: row.access_count as number | undefined,
    metadata: parseJson(row.metadata, undefined),
  };
}

function parseJson<T>(json: unknown, fallback: T): T {
  if (json === null || json === undefined || json === '') return fallback;
  // The columns are jsonb, so rows arrive already parsed. A string is a legacy
  // double-encoded value (written before toSupabaseFormat stopped stringifying).
  if (typeof json !== 'string') return json as T;
  try { return JSON.parse(json); } catch { return fallback; }
}
